/**
 * External dependencies
 */
import { withSelect } from '@wordpress/data';
import { date as formatTime } from '@wordpress/date';
import { compose } from '@wordpress/compose';
import { Component } from '@wordpress/element';
import { PropTypes } from 'prop-types';
import moment from 'moment';
import { filter, mapValues, transform } from 'lodash';
import { __ } from '@wordpress/i18n';
import classNames from 'classnames';
import { withViewportMatch } from '@wordpress/viewport';
import memorize from 'memorize-one';

/**
 * Internal dependencies
 */
import EventCalendarItem from '../../components/event-calendar-item';
import { getDayOfWeek, getCalStartEndTime, formatDateQueryLimits, DATE_RANGES } from '../../includes/date-utils';
import { EventTable } from '../event-table';

class EventCalendar extends Component {
	constructor( props ) {
		super( props );
		this.state = {
			selectedDate: moment().format( 'YYYY-MM-DD' ),
		};

		this.filterAvailability = memorize(
			( availability, dateRangeType, isSmallViewport ) => {
				if ( DATE_RANGES.THIS_WEEK === dateRangeType && ! isSmallViewport ) {
					return transformAvailabilityForWeekView( availability );
				}
				return transformAvailabilityForMonthView( availability );
			}
		);

		/*
		* Globally set locale for moment.
		*/
		moment.locale( wc_bookings_availability_args.locale );
	}

	getAvailability() {
		return this.filterAvailability( this.props.availability, this.props.dateRangeType, this.props.isSmallViewport );
	}

	renderHeader() {
		const { dateRangeType, minDate, isSmallViewport } = this.props;
		const weekView = dateRangeType === DATE_RANGES.THIS_WEEK;
		if ( weekView && isSmallViewport ) {
			return '';
		}
		const shift = weekView ? 1 : 0;
		const wpStartOfWeek = parseInt( wc_bookings_availability_args.start_of_week, 10 );
		const header = [];
		let startOfWeek = wpStartOfWeek === 0 ? 7 : wpStartOfWeek;
		const date = moment( minDate );
		for ( let i = 1 + shift; i <= 7 + shift; i++ ) {
			const style = {
				gridRow: '1 / 1',
				gridColumn: `${ i } / ${ i }`,
			};
			const day = startOfWeek++;
			startOfWeek = startOfWeek === 8 ? 1 : startOfWeek;
			const dayNumber = weekView ? ( ' ' + date.format( 'DD' ) ) : '';
			date.add( 1, 'day' );
			const key = `day-of-week-${ i }`;
			header.push( <div className="wc-bookings-availability-calendar-header-item" key={ key } style={ style } >{ getDayOfWeek( day ) + dayNumber }</div> );
		}

		return header;
	}

	renderCalendarMonthDays() {
		const { isPreview, isRequesting, minDate, maxDate, currentMonth, products, showSoldOut, isSmallViewport } = this.props;
		const availability = this.getAvailability();

		const calendarDays = [];
		let number = 0;

		let headerStyle = {};
		let dayStyle = {};

		// How many possible rows single calendar day has.
		const rowsPerDay = isSmallViewport ? 1 : 6;

		const start = moment( minDate );
		const end = moment( maxDate );
		while ( start.isSameOrBefore( end, 'day' ) ) {
			const column = ( number % 7 ) + 1;

			if ( ! isSmallViewport ) {
				const rowIndex = ~~( number / 7 ); // eslint-disable-line no-bitwise
				headerStyle = {
					gridColumn: `${ column } / ${ column }`,
					gridRow: `${ ( rowIndex * rowsPerDay ) + 2 } / span 1`,
				};
				dayStyle = {
					gridColumn: `${ column } / ${ column }`,
					gridRow: `${ ( rowIndex * rowsPerDay ) + 3 } / span ${ rowsPerDay - 1 }`,
				};
			}

			const date = moment( start ).format( 'YYYY-MM-DD' );
			const day = start.date();
			const dayString = ( day === 1 && ! isSmallViewport ? start.format( 'MMM' ) + ' ' : '' ) + day;

			const headerKey = `header-key-${ number }`;
			const dayKey = `day-key-${ number }`;

			const headerClass = classNames( 'wc-bookings-availability-cal-date', {
				'wc-bookings-availability-cal-date-other-month': ! moment( date ).isSame( currentMonth, 'month' ),
				'wc-bookings-availability-first-in-row': 1 === column,
				'wc-bookings-availability-selected-date': this.state.selectedDate === date,
				'wc-bookings-availability-has-no-items': ! availability[ date ] && ! isRequesting,
			} );
			if ( isSmallViewport ) {
				calendarDays.push(
					<span
						role="button"
						tabIndex="0"
						onKeyDown={ ( e ) => 'Enter' === e.key || ' ' === e.key ? this.setSelectedDate( date ) : '' }
						onClick={ () => this.setSelectedDate( date ) }
						key={ headerKey }
						style={ headerStyle }
						className={ headerClass }>
						{ dayString }
					</span> );
			} else {
				calendarDays.push( <span key={ headerKey }
					style={ headerStyle } className={ headerClass }>{ dayString }</span> );
			}

			const dayClassName = classNames( 'wc-bookings-availability-calendar-day', { 'wc-bookings-availability-first-in-row': 1 === column } );

			if ( ! isSmallViewport ) {
				let dayMarkup;

				if ( isRequesting ) {
					dayMarkup = (
						<div key={ dayKey } style={ dayStyle } className={ dayClassName }>
							<div className={ 'wc-bookings-availability-calendar-day-item wc-bookings-availability-calendar-day-item-placeholder' }>
								&nbsp;
							</div>
						</div>
					);
				} else {
					dayMarkup = (
						<div key={ dayKey } style={ dayStyle } className={ dayClassName }>
							{ productsWithAvailabilityOnDay( products, availability, date ).map( ( [ product, productAvailability ], index ) => (
								<EventCalendarItem
									isPreview={ isPreview }
									showSoldOut={ showSoldOut }
									key={ index }
									availability={ productAvailability }
									isRequesting={ isRequesting }
									product={ product }
									date={ start.toDate() }
								/>
							) ) }
						</div>
					);
				}

				calendarDays.push( dayMarkup );
			}

			// Loop increments.
			number++;
			start.add( 1, 'day' );
		}

		return calendarDays;
	}

	renderCalendarWeekDays( ) {
		const { minDate, maxDate, isRequesting, isPreview, products, showSoldOut } = this.props;

		// Add placeholder availability while requesting.
		const availability = ( ! isRequesting ) ? this.getAvailability() : { '00': null };

		const calendarItems = [];
		const format = wc_bookings_availability_args.time_format.replace( ':i', '' );
		for ( const hour in availability ) {
			const hourRow = parseInt( hour ) + 2;
			const hourStyle = {
				gridColumn: `${ 1 } / ${ 1 }`,
				gridRow: `${ hourRow } /  ${ hourRow }`,
			};
			const time = formatTime( format, moment().hour( hour ) );
			const hourKey = `hour-key-${ hour }`;
			// Hide hour label while requesting.
			const hourColumnMarkup = ( ! isRequesting ) ? (
				<span className="wc-bookings-availability-calendar-week-hour"style={ hourStyle } key={ hourKey } >{ time }</span>
			) : null;

			// Hours column.
			calendarItems.push( hourColumnMarkup );

			const start = moment( minDate );
			const end = moment( maxDate );
			let column = 1; // Column 1 is for hours.
			const days = availability[ hour ] || {};
			while ( start.isSameOrBefore( end, 'day' ) ) {
				const date = moment( start ).format( 'YYYY-MM-DD' );
				column++;

				const hourDateKey = `hour-day-key-${ hour }-${ date }`;
				const hourDateStyle = {
					gridColumn: `${ column } / ${ column }`,
					gridRow: `${ hourRow } /  ${ hourRow }`,
				};

				if ( isRequesting ) {
					// Loading placeholder.
					calendarItems.push(
						<div key={ hourDateKey } style={ hourDateStyle } className="wc-bookings-availability-calendar-day">
							<div className={ 'wc-bookings-availability-calendar-day-item wc-bookings-availability-calendar-day-item-placeholder' }>
								&nbsp;
							</div>
						</div>
					);
					// Advance to the next day.
					start.add( 1, 'day' );
					continue;
				}

				if ( ! ( date in days ) ) {
					// Nothing todo here. Use empty container ( for styling ) and go to the next day.
					calendarItems.push( <span key={ hourDateKey } style={ hourDateStyle } className="wc-bookings-availability-calendar-day" /> );
					// Advance to the next day.
					start.add( 1, 'day' );
					continue;
				}

				const slots = availability[ hour ][ date ];
				const loadMore = slots.length < availability[ hour ][ date ].length;

				const hourMarkup = (
					<div key={ hourDateKey } style={ hourDateStyle } className="wc-bookings-availability-calendar-day">
						{ productsWithAvailabilityOnDayHour( products, slots ).map( ( [ product, productAvailability ], index ) => (
							<EventCalendarItem
								isPreview={ isPreview }
								showSoldOut={ showSoldOut }
								key={ index }
								availability={ productAvailability }
								isRequesting={ isRequesting }
								product={ product }
								date={ start.toDate() }
							/>
						) ) }
						{ loadMore && <div>{ 'Load more.' }</div> }
					</div>
				);

				calendarItems.push( hourMarkup );
				// Advance to the next day.
				start.add( 1, 'day' );
			}
		}
		return calendarItems;
	}

	renderCalendarWeekDaysSmallViewport() {
		const { minDate, maxDate, isRequesting } = this.props;

		const availability = transformAvailabilityForMonthView( this.props.availability );
		const start = moment( minDate );
		const end = moment( maxDate );

		const calendarDays = [];

		while ( start.isSameOrBefore( end, 'day' ) ) {
			const date = moment( start ).format( 'YYYY-MM-DD' );

			const day = start.date();
			const dayString = start.format( 'ddd' );

			const headerKey = `header-key-${ day }`;

			const headerClass = classNames( 'wc-bookings-availability-cal-date', {
				'wc-bookings-availability-selected-date': this.state.selectedDate === date,
				'wc-bookings-availability-has-no-items': ! availability[ date ] && ! isRequesting,
			} );

			calendarDays.push(
				<span
					role="button"
					tabIndex="0"
					onKeyDown={ ( e ) => 'Enter' === e.key || ' ' === e.key ? this.setSelectedDate( date ) : '' }
					onClick={ () => this.setSelectedDate( date ) }
					key={ headerKey }
					className={ headerClass }>
					{ dayString }<br />{ day }
				</span> );

			start.add( 1, 'day' );
		}
		return calendarDays;
	}

	renderDays() {
		const { dateRangeType, isSmallViewport } = this.props;
		if ( DATE_RANGES.THIS_WEEK === dateRangeType ) {
			if ( isSmallViewport ) {
				return this.renderCalendarWeekDaysSmallViewport();
			}
			return this.renderCalendarWeekDays();
		}
		return this.renderCalendarMonthDays();
	}

	renderDaysItems() {
		const { isRequesting, products } = this.props;
		const availability = this.getAvailability();

		const date = this.state.selectedDate;

		return ! isRequesting ?
			<EventTable
				availabilityQuery={ { minDate: date, maxDate: date } }
				availability={ availability[ date ] || [] }
				products={ products }
				isRequesting={ isRequesting }
				remainingRecords={ 0 }
				infiniteScrollEnabled={ false }
				isLastPage={ true }
			/> : '';
	}

	setSelectedDate( date ) {
		this.setState( {
			selectedDate: date,
		} );
	}

	render() {
		const { dateRangeType, isError, isSmallViewport, isRequesting } = this.props;

		const calendarClass = classNames( 'wc-bookings-availability-calendar', {
			'wc-bookings-availability-calendar-week-view': DATE_RANGES.THIS_WEEK === dateRangeType,
			'wc-bookings-availability-calendar-small': isSmallViewport,
			'wc-bookings-availability-calendar-requesting': isRequesting,
		} );

		const calendarContainerClass = classNames( 'wc-bookings-availability-calendar-container', {
			'wc-bookings-availability-calendar-container-week-view': DATE_RANGES.THIS_WEEK === dateRangeType,
			'wc-bookings-availability-calendar-container-month-view': DATE_RANGES.THIS_MONTH === dateRangeType,
			'wc-bookings-availability-calendar-container-small': isSmallViewport,
		} );

		const reloadWindow = () => {
			window.location.reload();
		};

		return isError ?
			(
				<div className="woocommerce-error notice notice-error">
					{ __( 'We weren\'t able to load the store calendar.', 'woocommerce-bookings-availability' ) }
					{ ' ' }
					<a href="#" onClick={ reloadWindow }>{ __( 'Try again', 'woocommerce-bookings-availability' ) }</a> { /* eslint-disable-line jsx-a11y/anchor-is-valid */ }
				</div>
			) : (
				<div>
					<div className={ calendarContainerClass }>
						<div className={ calendarClass }>
							{ this.renderHeader() }
							{ this.renderDays() }
							{ isRequesting ? <div className="wc-bookings-availability-calendar-container-loading-animation"><div></div></div> : '' }
						</div>
					</div>
					{
						isSmallViewport ? this.renderDaysItems() : ''
					}
				</div>
			);
	}
}

EventCalendar.defaultProps = {
	availability: {},
	productIds: [],
	categoryIds: [],
	offset: 0,
	isSmallViewport: false,
};

EventCalendar.propTypes = {
	/**
	 * Object of availability data in use by EventItem.
	 */
	availability: PropTypes.array.isRequired,
	/**
	 * Sets up component for in-editor preview if true.
	 */
	isPreview: PropTypes.bool,
	/**
	 * Whether the request is still processing.
	 */
	isRequesting: PropTypes.bool,
	/**
	 * Lowest date displayed at the calendar.
	 */
	minDate: PropTypes.object,
	/**
	 * Highest date displayed at the calendar.
	 */
	maxDate: PropTypes.object,
	/**
	 * Currently displayed month.
	 */
	currentMonth: PropTypes.object,
	/**
	 * Object of product objects indexed by product id.
	 */
	products: PropTypes.oneOfType( [
		PropTypes.object.isRequired,
		PropTypes.array.isRequired,
	] ),

	/**
	 * Date range type (month, week).
	 */
	dateRangeType: PropTypes.string,

	/**
	 * Display sold out bookings.
	 */
	showSoldOut: PropTypes.bool,

	/**
	 * If its an error.
	 */
	isError: PropTypes.bool,

	/**
	 * Whether the current device has a small screen or window.
	 */
	isSmallViewport: PropTypes.bool,
};

const productsWithAvailabilityOnDay = function( products, availability, date ) {
	// Don't generate artifical entries.
	if ( ! ( date in availability ) ) {
		return [];
	}
	const dayAvailability = availability[ date ];
	return transform( products, ( result, product, productId ) => {
		const productAvailability = filter( dayAvailability, ( slot ) => {
			return slot.productId === parseInt( productId );
		} );
		if ( productAvailability.length > 0 ) {
			result.push( [ { id: productId, ...product }, productAvailability ] );
		}
		return result;
	}, [] );
};

const productsWithAvailabilityOnDayHour = function( products, availability ) {
	return transform( products, ( result, product, productId ) => {
		const productAvailability = filter( availability, ( slot ) => {
			return slot.productId === parseInt( productId );
		} );

		if ( productAvailability.length > 0 ) {
			result.push( [ { id: productId, ...product }, productAvailability ] );
		}

		return result;
	}, [] );
};

const transformAvailabilityForMonthView = ( availability ) => {
	const availabilityByDays = {};
	for ( const slot of availability ) {
		const key = moment( slot.date ).format( 'YYYY-MM-DD' );
		! ( key in availabilityByDays ) && ( availabilityByDays[ key ] = [] ); // eslint-disable-line no-unused-expressions
		availabilityByDays[ key ].push( slot );
	}

	return mapValues( availabilityByDays, ( day ) => (
		day.sort( ( a, b ) => moment( a.date ).diff( moment( b.date ) ) )
	) );
};

const transformAvailabilityForWeekView = ( availability ) => {
	/**
	 * Availability structure
	 * slot_hour{}:days{}:slots.
	 * slots are time sorted
	 */
	const availabilityByHour = {};
	for ( const slot of availability ) {
		const hour = moment( slot.date ).format( 'HH' );
		const day = moment( slot.date ).format( 'YYYY-MM-DD' );
		! ( hour in availabilityByHour ) && ( availabilityByHour[ hour ] = {} ); // eslint-disable-line no-unused-expressions
		! ( day in availabilityByHour[ hour ] ) && ( availabilityByHour[ hour ][ day ] = [] ); // eslint-disable-line no-unused-expressions
		availabilityByHour[ hour ][ day ].push( slot );
	}

	return availabilityByHour; // this should be sorted if REST sends in sorted order.
};

export default compose( [
	withSelect( ( select, props ) => {
		const {
			getAvailability,
			isGetAvailabilityError,
			isGetAvailabilityRequesting,
			getProducts,
			isGetProductsRequesting,
			isGetProductsError,
		} = select( 'woocommerce-bookings-availability' );
		const { productIds, categoryIds, resourceIds, dateRangeType, offset } = props;

		const startEndTimes = getCalStartEndTime( dateRangeType, offset );
		const { minDate, maxDate } = formatDateQueryLimits( startEndTimes.minDate, startEndTimes.maxDate );

		const availabilityQuery = {
			productIds,
			categoryIds,
			resourceIds,
			minDate,
			maxDate,
		};

		const productsQuery = {
			productIds,
		};

		const products = getProducts( productsQuery );
		const availability = getAvailability( availabilityQuery );
		const isError = isGetAvailabilityError( availabilityQuery ) || isGetProductsError();
		const isRequesting = isGetAvailabilityRequesting( availabilityQuery ) || isGetProductsRequesting();

		return { availability, isError, isRequesting, ...startEndTimes, products, dateRangeType };
	} ),
	withViewportMatch( { isSmallViewport: '< small' } ),
] )( EventCalendar );

// export lower order component to expose it for unit testing.
export { EventCalendar };
